import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchMovement, formatCurrency, handleError, getDescription } from '../../utils/lib';
import { useLocation, useNavigate } from 'react-router-dom';
import ViewPage from './ViewPage';
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useConfig } from '../../providers/ConfigProvider';
import BottomSheet from '../../layouts/BottomSheet';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import ListItemProductRemove from '../../layouts/ListItemProductRemove';
import InventoryIcon from '@mui/icons-material/Inventory';
import Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';

function ViewMovement() {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [id, setId] = useState(location.state.id);
  const [movement, setMovement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {updateTitle} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const movement = await fetchMovement(id);
        setMovement(movement);
        updateTitle('', t('view.movementTitle'));
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}internal_movements?id=${movement.id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'IN_TRANSIT':
        return <Chip label={t('view.inTransit')} color="warning" size="small" />;
      case 'COMPLETED':
        return <Chip label={t('view.completed')} color="success" size="small" />;
      case 'CANCELLED':
        return <Chip label={t('view.cancelledChip')} color="error" size="small" />;
      default:
        return <Chip label={status || '-'} size="small" />;
    }
  };

  if (isLoading || isLoadingConfig || !movement) {
    return <ViewPage isLoading={true} />;
  }

  return (
    <ViewPage 
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ px: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography>{t('view.sellFieldDate')} {new Date(movement.date).toLocaleString()}</Typography>
          <Typography>{t('view.sellFieldUser')} {movement.user.name}</Typography>
          <Typography>{t('view.movementFieldReceipt')} {movement.receipt}</Typography>
          {movement.origin_deposit && (
            <Typography>{t('view.originDeposit', { name: movement.origin_deposit.name })}</Typography>
          )}
          {movement.destination_deposit && (
            <Typography>{t('view.destinationDeposit', { name: movement.destination_deposit.name })}</Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography component="span">{t('view.statusLabel')} </Typography>
            {getStatusChip(movement.type)}
          </Box>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{t('common.products')}</Typography>
          <Box>
            {movement.items?.map((item, index) => (
              <ListItemProductRemove
                key={index}
                product={item.product.name}
                description={`${new Decimal(item.quantity).toDecimalPlaces(2, Decimal.ROUND_HALF_UP)} ${item.product.unit?.first_unit_string || 'u'}`}
                subtotal={t('view.quantityLabel', { qty: new Decimal(item.quantity).toDecimalPlaces(2, Decimal.ROUND_HALF_UP) })}
                index={index}
                thumbUrl={item.product.thumb_url}
                avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.deleteInternalMovementTitle')}</DialogTitle>
        <DialogContent>
          {t('view.deleteInternalMovementMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <BottomSheet 
        zIndex={1001}
        buttonGroups={[
          {
            label: t('common.actions'), 
            buttons: [
              ...(config.permissions.actions_permissions.internal_movements?.delete ? [{
                label: t('common.delete'),
                action: handleDeleteOpen,
                icon: <DeleteIcon />
              }] : []),
            ].filter(Boolean)
          }
        ]}
      />
    </ViewPage>
  );
}

export default ViewMovement; 